import {
  Folder,
  Requirement,
  Status,
} from "../../redux/interfaces/folder.types.ts";
import { findRequirement } from "./findRequirement.ts";

export type DerivedAnswer = {
  value: boolean;
  sourceLabel: string;
};

export type AnalysisDerivations = Partial<Record<string, DerivedAnswer>>;

const TENANT_SUBFOLDER_PREFIX = "tenant-";
const GUARANTOR_SUBFOLDER_PREFIX = "guarantor-";
const COLLATERAL_SUBFOLDER_SLUG = "collateral";
const LEASE_CONTRACT_SUBFOLDER_PREFIX = "lease-contract";
const PM_SUBFOLDER_INFIX = "-pm";

const OFFICIAL_ID_REQUIREMENT_SLUG = "official-id";
const PROOF_OF_INCOME_REQUIREMENT_SLUG = "proof-of-income";
const SAT_COMPLIANCE_REQUIREMENT_SLUG = "sat-compliance";
const POWER_OF_ATTORNEY_REQUIREMENT_SLUG = "power-of-attorney";

const isTenantSubfolder = (slug: string) =>
  slug.startsWith(TENANT_SUBFOLDER_PREFIX);
const isGuarantorSubfolder = (slug: string) =>
  slug.startsWith(GUARANTOR_SUBFOLDER_PREFIX);
const isPmSubfolder = (slug: string) => slug.includes(PM_SUBFOLDER_INFIX);
const isTenantPmSubfolder = (slug: string) =>
  isTenantSubfolder(slug) && isPmSubfolder(slug);
const isGuarantorPmSubfolder = (slug: string) =>
  isGuarantorSubfolder(slug) && isPmSubfolder(slug);

const requirementValidity = (
  req: Requirement | undefined,
): boolean | undefined => {
  if (!req) return undefined;
  if (req.status === Status.Valid) return true;
  if (req.status === Status.Invalid) return false;
  return undefined;
};

const findFormValue = (
  folder: Folder,
  subfolderSlugMatch: (slug: string) => boolean,
  key: string,
): string | undefined => {
  const subfolder = folder.subfolders?.find((sf) =>
    subfolderSlugMatch(sf.slug),
  );
  if (!subfolder) return undefined;
  for (const req of subfolder.requirements) {
    const fv = req.form_values?.find((v) => v.key === key);
    if (fv?.value) return fv.value;
  }
  return undefined;
};

const isMexicoNationality = (value: string): boolean => {
  const normalized = value.trim().toLowerCase();
  return normalized === "mexico" || normalized === "méxico";
};

export function deriveAnalysisDefaults(folder: Folder): AnalysisDerivations {
  const out: AnalysisDerivations = {};
  const subfolders = folder.subfolders ?? [];

  // ── v1 ──────────────────────────────────────────────────────────────

  out.has_guarantor = {
    value: subfolders.some((sf) => isGuarantorSubfolder(sf.slug)),
    sourceLabel: "Subexpediente del fiador",
  };

  out.has_property_under_guarantee = {
    value: subfolders.some((sf) => sf.slug === COLLATERAL_SUBFOLDER_SLUG),
    sourceLabel: "Subexpediente del inmueble en garantía",
  };

  const nationality = findFormValue(folder, isTenantSubfolder, "nationality");
  if (nationality) {
    out.is_foreign = {
      value: !isMexicoNationality(nationality),
      sourceLabel: "Nacionalidad del inquilino",
    };
  }

  const tenantIdReq = findRequirement(
    folder,
    isTenantSubfolder,
    OFFICIAL_ID_REQUIREMENT_SLUG,
  );
  const tenantIdValid = requirementValidity(tenantIdReq);
  if (tenantIdValid !== undefined) {
    out.valid_id = {
      value: tenantIdValid,
      sourceLabel: "Identificación del inquilino",
    };
  }

  const hasGuarantor = subfolders.some((sf) => isGuarantorSubfolder(sf.slug));
  if (hasGuarantor) {
    const guarantorIdReq = findRequirement(
      folder,
      isGuarantorSubfolder,
      OFFICIAL_ID_REQUIREMENT_SLUG,
    );
    const guarantorIdValid = requirementValidity(guarantorIdReq);
    if (guarantorIdValid !== undefined) {
      out.valid_guarantor_id = {
        value: guarantorIdValid,
        sourceLabel: "Identificación del fiador",
      };
    }
  }

  // ── tier 1 ──────────────────────────────────────────────────────────

  const proofOfIncomeReq = findRequirement(
    folder,
    isTenantSubfolder,
    PROOF_OF_INCOME_REQUIREMENT_SLUG,
  );
  const proofOfIncomeValid = requirementValidity(proofOfIncomeReq);
  if (proofOfIncomeValid !== undefined) {
    out.valid_income = {
      value: proofOfIncomeValid,
      sourceLabel: "Comprobante de ingresos del inquilino",
    };
  }

  // The system models real-estate collateral only — no vehicle/machinery
  // subfolder type exists, so this answer defaults to No.
  out.vehicle_under_guarantee = {
    value: false,
    sourceLabel: "Sin registro de vehículo o maquinaria en garantía",
  };

  out.has_lease_contract = {
    value: subfolders.some((sf) =>
      sf.slug.startsWith(LEASE_CONTRACT_SUBFOLDER_PREFIX),
    ),
    sourceLabel: "Subexpediente de Contrato de Arrendamiento",
  };

  // ── tier 2 ──────────────────────────────────────────────────────────

  // Foreigners' only valid ID option in the catalog is "Forma Migratoria"
  // (INE/passport/driver-license all require Mexican citizenship). So a
  // verified ID for a foreign tenant implies a valid migration form.
  if (out.is_foreign?.value === true && tenantIdValid === true) {
    out.valid_migration_form = {
      value: true,
      sourceLabel: "Identificación verificada del inquilino extranjero",
    };
  }

  const tenantOrGuarantorIsPm =
    subfolders.some((sf) => isTenantPmSubfolder(sf.slug)) ||
    subfolders.some((sf) => isGuarantorPmSubfolder(sf.slug));

  if (tenantOrGuarantorIsPm) {
    const satReq =
      findRequirement(folder, isTenantPmSubfolder, SAT_COMPLIANCE_REQUIREMENT_SLUG) ??
      findRequirement(folder, isGuarantorPmSubfolder, SAT_COMPLIANCE_REQUIREMENT_SLUG);
    const satValid = requirementValidity(satReq);
    if (satValid !== undefined) {
      out.compliance_opinion_positive = {
        value: satValid,
        sourceLabel: "Opinión de cumplimiento del SAT",
      };
    }

    const poaReq =
      findRequirement(folder, isTenantPmSubfolder, POWER_OF_ATTORNEY_REQUIREMENT_SLUG) ??
      findRequirement(folder, isGuarantorPmSubfolder, POWER_OF_ATTORNEY_REQUIREMENT_SLUG);
    const poaValid = requirementValidity(poaReq);
    if (poaValid !== undefined) {
      out.representative_has_admin_permissions = {
        value: poaValid,
        sourceLabel: "Poder del representante legal",
      };
    }
  }

  return out;
}
